import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Upload, Image as ImageIcon, MapPin, Phone, Mail, Globe, Link as LinkIcon, Clock, IdCard, PoundSterling } from "lucide-react";
import PortalShell from "@/components/PortalShell";
import { portalApi, portalMe } from "@/lib/portalApi";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const emptyVenue = {
  name: "",
  slug: "",
  email: "",
  phone: "",
  address: { line1: "", line2: "", city: "", postcode: "", country: "United Kingdom" },
  opening_hours: DAYS.map((_, day) => ({ day, open: "", close: "", closed: false })),
  fabswingers_profile: "",
  swinghub_profile: "",
  memberships_enabled: false,
  membership_number_prefix: "M-",
  membership_duration_type: "annual",
  membership_duration_months: "",
  membership_pricing: {},
  hide_booking_fees: false,
};

function normaliseHours(hours) {
  const byDay = new Map((hours || []).map((h) => [h.day, h]));
  return DAYS.map((_, day) => {
    const h = byDay.get(day) || {};
    return {
      day,
      open: h.open || "",
      close: h.close || "",
      closed: !!h.closed,
    };
  });
}

export default function PortalVenue() {
  const [me, setMe] = useState(null);
  const [form, setForm] = useState(emptyVenue);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const logoRef = useRef(null);
  const coverRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const meRes = await portalMe();
      setMe(meRes);
      const v = meRes.venue || {};
      setForm({
        name: v.name || "",
        slug: v.slug || "",
        email: v.email || "",
        phone: v.phone || "",
        address: {
          line1: v.address?.line1 || "",
          line2: v.address?.line2 || "",
          city: v.address?.city || "",
          postcode: v.address?.postcode || "",
          country: v.address?.country || "United Kingdom",
        },
        opening_hours: normaliseHours(v.opening_hours),
        fabswingers_profile: v.fabswingers_profile || "",
        swinghub_profile: v.swinghub_profile || "",
        memberships_enabled: !!v.memberships_enabled,
        membership_number_prefix: v.membership_number_prefix || "M-",
        membership_duration_type: v.membership_duration_type || "annual",
        membership_duration_months: v.membership_duration_months || "",
        membership_pricing: v.membership_pricing || {},
        hide_booking_fees: !!v.hide_booking_fees,
      });
    } catch {
      toast.error("Unable to load venue info.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setField = (path, value) => {
    setForm((f) => {
      const next = { ...f };
      const keys = path.split(".");
      let ptr = next;
      for (let i = 0; i < keys.length - 1; i += 1) {
        ptr[keys[i]] = { ...ptr[keys[i]] };
        ptr = ptr[keys[i]];
      }
      ptr[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const setHours = (day, patch) => {
    setForm((f) => ({
      ...f,
      opening_hours: f.opening_hours.map((h) => (h.day === day ? { ...h, ...patch } : h)),
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim() || undefined,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        address: {
          line1: form.address.line1.trim(),
          line2: form.address.line2.trim() || null,
          city: form.address.city.trim(),
          postcode: form.address.postcode.trim(),
          country: form.address.country.trim() || "United Kingdom",
        },
        opening_hours: form.opening_hours.map((h) => ({
          day: h.day,
          open: h.closed ? null : (h.open || null),
          close: h.closed ? null : (h.close || null),
          closed: !!h.closed,
        })),
        fabswingers_profile: form.fabswingers_profile.trim() || null,
        swinghub_profile: form.swinghub_profile.trim() || null,
        memberships_enabled: !!form.memberships_enabled,
        membership_number_prefix: (form.membership_number_prefix || "").trim() || "M-",
        membership_duration_type: form.membership_duration_type || "annual",
        membership_duration_months: form.membership_duration_months
          ? Number(form.membership_duration_months)
          : null,
        membership_pricing: Object.fromEntries(
          Object.entries(form.membership_pricing || {})
            .filter(([, v]) => v !== "" && Number(v) > 0)
            .map(([k, v]) => [k, Number(v)])
        ),
        hide_booking_fees: !!form.hide_booking_fees,
      };
      const { data } = await portalApi.patch("/portal/venue", payload);
      toast.success("Venue info saved.");
      // Refresh from response
      setMe((m) => ({ ...(m || {}), venue: data }));
      setForm((f) => ({ ...f, slug: data.slug || f.slug }));
    } catch (err) {
      const detail = err?.response?.data?.detail || "Save failed.";
      toast.error(typeof detail === "string" ? detail : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (f, kind) => {
    if (!f) return;
    const setUploading = kind === "logo" ? setUploadingLogo : setUploadingCover;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await portalApi.post(`/portal/venue/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMe((m) => ({ ...(m || {}), venue: data }));
      toast.success(`${kind === "logo" ? "Logo" : "Cover photo"} updated.`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Upload failed.";
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const venue = me?.venue || {};

  return (
    <PortalShell me={me} testId="portal-venue-page">
      <div className="max-w-[1100px] mx-auto px-6 md:px-8 py-10">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-3">Venue settings</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">Update venue information</h1>
          <p className="mt-3 text-sm text-[#A3A3A3] max-w-2xl">
            This is what customers see on your public venue page, in tickets, and in every email
            you send from Swingle. Keep it accurate.
          </p>
        </div>

        {loading ? (
          <div className="py-20 flex items-center justify-center">
            <Loader2 size={20} className="animate-spin text-[#D9A6F5]" />
          </div>
        ) : (
          <>
            {/* Media panels */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
              <MediaCard
                label="Logo"
                testId="venue-media-logo"
                src={venue.logo_data_uri}
                uploading={uploadingLogo}
                onPick={() => logoRef.current?.click()}
                aspect="aspect-square"
              />
              <MediaCard
                label="Cover photo"
                testId="venue-media-cover"
                src={venue.cover_data_uri}
                uploading={uploadingCover}
                onPick={() => coverRef.current?.click()}
                aspect="aspect-[16/9]"
              />
              <input
                ref={logoRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                data-testid="venue-logo-file"
                onChange={(e) => uploadImage(e.target.files?.[0] || null, "logo")}
              />
              <input
                ref={coverRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                data-testid="venue-cover-file"
                onChange={(e) => uploadImage(e.target.files?.[0] || null, "cover")}
              />
            </div>

            <form onSubmit={save} className="space-y-10">
              {/* Basics */}
              <Section title="The basics">
                <Grid>
                  <Field label="Venue name">
                    <input
                      required
                      type="text"
                      value={form.name}
                      onChange={(e) => setField("name", e.target.value)}
                      data-testid="venue-name"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="URL slug" hint={venue.slug ? `Public URL: /v/${form.slug || venue.slug}` : ""}>
                    <input
                      type="text"
                      value={form.slug}
                      onChange={(e) => setField("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                      data-testid="venue-slug"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Contact email" icon={<Mail size={12} />}>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setField("email", e.target.value)}
                      data-testid="venue-email"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Phone" icon={<Phone size={12} />}>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setField("phone", e.target.value)}
                      data-testid="venue-phone"
                      className={inputCls}
                    />
                  </Field>
                </Grid>
              </Section>

              {/* Address */}
              <Section title="Address" icon={<MapPin size={14} />}>
                <Grid>
                  <Field label="Address line 1" full>
                    <input
                      required
                      type="text"
                      value={form.address.line1}
                      onChange={(e) => setField("address.line1", e.target.value)}
                      data-testid="venue-address-line1"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Address line 2 (optional)" full>
                    <input
                      type="text"
                      value={form.address.line2}
                      onChange={(e) => setField("address.line2", e.target.value)}
                      data-testid="venue-address-line2"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="City">
                    <input
                      required
                      type="text"
                      value={form.address.city}
                      onChange={(e) => setField("address.city", e.target.value)}
                      data-testid="venue-address-city"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Postcode">
                    <input
                      required
                      type="text"
                      value={form.address.postcode}
                      onChange={(e) => setField("address.postcode", e.target.value.toUpperCase())}
                      data-testid="venue-address-postcode"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Country" full>
                    <input
                      type="text"
                      value={form.address.country}
                      onChange={(e) => setField("address.country", e.target.value)}
                      data-testid="venue-address-country"
                      className={inputCls}
                    />
                  </Field>
                </Grid>
              </Section>

              {/* Opening hours */}
              <Section title="Opening hours" icon={<Clock size={14} />}>
                <div className="space-y-2">
                  {form.opening_hours.map((h) => (
                    <div
                      key={h.day}
                      data-testid={`venue-hours-row-${h.day}`}
                      className="grid grid-cols-[60px_1fr_1fr_auto] gap-3 items-center"
                    >
                      <div className="text-xs uppercase tracking-[0.24em] text-[#A3A3A3]">
                        {DAYS[h.day]}
                      </div>
                      <input
                        type="time"
                        value={h.open || ""}
                        disabled={h.closed}
                        onChange={(e) => setHours(h.day, { open: e.target.value })}
                        data-testid={`venue-hours-open-${h.day}`}
                        className={inputCls + " disabled:opacity-40"}
                      />
                      <input
                        type="time"
                        value={h.close || ""}
                        disabled={h.closed}
                        onChange={(e) => setHours(h.day, { close: e.target.value })}
                        data-testid={`venue-hours-close-${h.day}`}
                        className={inputCls + " disabled:opacity-40"}
                      />
                      <label className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={h.closed}
                          onChange={(e) => setHours(h.day, { closed: e.target.checked })}
                          data-testid={`venue-hours-closed-${h.day}`}
                          className="h-4 w-4 accent-[#D9A6F5]"
                        />
                        Closed
                      </label>
                    </div>
                  ))}
                </div>
              </Section>

              {/* Profile links */}
              <Section title="Profiles (optional)" icon={<LinkIcon size={14} />}>
                <Grid>
                  <Field label="Fabswingers profile URL" icon={<Globe size={12} />}>
                    <input
                      type="url"
                      placeholder="https://…"
                      value={form.fabswingers_profile}
                      onChange={(e) => setField("fabswingers_profile", e.target.value)}
                      data-testid="venue-fabswingers"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Swinghub profile URL" icon={<Globe size={12} />}>
                    <input
                      type="url"
                      placeholder="https://…"
                      value={form.swinghub_profile}
                      onChange={(e) => setField("swinghub_profile", e.target.value)}
                      data-testid="venue-swinghub"
                      className={inputCls}
                    />
                  </Field>
                </Grid>
              </Section>

              {/* Checkout display — booking fees */}
              <Section title="Checkout & fees" icon={<PoundSterling size={14} />}>
                <label
                  className="flex items-start gap-4 border border-white/10 hover:border-[#D9A6F5]/40 p-4 cursor-pointer transition-colors"
                  data-testid="venue-hide-booking-fees-wrap"
                >
                  <input
                    type="checkbox"
                    checked={!!form.hide_booking_fees}
                    onChange={(e) => setField("hide_booking_fees", e.target.checked)}
                    data-testid="venue-hide-booking-fees-toggle"
                    className="mt-1 h-4 w-4 accent-[#D9A6F5]"
                  />
                  <div>
                    <div className="text-sm text-[#F5F5F0]">
                      Hide booking fees at checkout
                    </div>
                    <div className="mt-1 text-[11px] text-[#A3A3A3] leading-relaxed">
                      Every ticket & membership price will be bumped by{" "}
                      <span className="text-[#D9A6F5] font-bold">£2</span> and the separate
                      booking fee line will be removed from your customers&apos; checkout. Great
                      for venues who prefer buyers see one clean all-inclusive price. You keep
                      the same take-home per ticket — Swingle still absorbs Stripe processing fees.
                    </div>
                  </div>
                </label>
              </Section>

              {/* Digital memberships */}
              <Section title="Digital memberships" icon={<IdCard size={14} />}>
                <label
                  className="flex items-start gap-4 border border-white/10 hover:border-[#D9A6F5]/40 p-4 cursor-pointer transition-colors"
                  data-testid="venue-memberships-toggle-wrap"
                >
                  <input
                    type="checkbox"
                    checked={!!form.memberships_enabled}
                    onChange={(e) => setField("memberships_enabled", e.target.checked)}
                    data-testid="venue-memberships-toggle"
                    className="mt-1 h-4 w-4 accent-[#D9A6F5]"
                  />
                  <div>
                    <div className="text-sm text-[#F5F5F0]">
                      Enable digital memberships for this venue
                    </div>
                    <div className="mt-1 text-[11px] text-[#A3A3A3] leading-relaxed">
                      Turns on the <span className="text-[#D9A6F5]">Members</span> section in
                      your portal, lets you import your existing member list, and lets you flag
                      individual events as members-only with custom pricing per membership type.
                    </div>
                  </div>
                </label>

                {form.memberships_enabled && (
                  <div className="mt-6 space-y-4 border border-white/10 p-4">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[#D9A6F5]">
                      Sell memberships on your public page
                    </div>
                    <Grid>
                      <Field label="Membership # prefix (max 10 chars)">
                        <input
                          type="text"
                          maxLength={10}
                          placeholder="e.g. SC-"
                          value={form.membership_number_prefix || ""}
                          onChange={(e) => setField("membership_number_prefix", e.target.value)}
                          data-testid="venue-membership-prefix"
                          className={inputCls}
                        />
                      </Field>
                      <Field label="Duration">
                        <select
                          value={form.membership_duration_type || "annual"}
                          onChange={(e) => setField("membership_duration_type", e.target.value)}
                          data-testid="venue-membership-duration"
                          className={inputCls}
                        >
                          <option value="annual">Annual (1 year)</option>
                          <option value="lifetime">Lifetime</option>
                          <option value="custom_months">Custom (months)</option>
                        </select>
                      </Field>
                      {form.membership_duration_type === "custom_months" && (
                        <Field label="Duration in months">
                          <input
                            type="number"
                            min="1"
                            max="120"
                            value={form.membership_duration_months || ""}
                            onChange={(e) => setField("membership_duration_months", e.target.value)}
                            data-testid="venue-membership-months"
                            className={inputCls}
                          />
                        </Field>
                      )}
                    </Grid>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[#A3A3A3] mt-4 mb-2">
                      Prices per membership type (leave blank to hide that type)
                    </div>
                    <Grid>
                      {[
                        { id: "single_guy", label: "Single Man" },
                        { id: "single_lady", label: "Single Lady" },
                        { id: "couple", label: "Couple" },
                        { id: "tv", label: "TV" },
                      ].map((m) => (
                        <Field key={m.id} label={m.label}>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] text-sm">£</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="0.00"
                              value={form.membership_pricing?.[m.id] ?? ""}
                              onChange={(e) =>
                                setField("membership_pricing", {
                                  ...(form.membership_pricing || {}),
                                  [m.id]: e.target.value === "" ? "" : Number(e.target.value),
                                })
                              }
                              data-testid={`venue-membership-price-${m.id}`}
                              className={`${inputCls} pl-7`}
                            />
                          </div>
                        </Field>
                      ))}
                    </Grid>
                  </div>
                )}
              </Section>

              {/* Save bar */}
              <div className="sticky bottom-0 -mx-6 md:-mx-8 px-6 md:px-8 py-4 bg-black/90 backdrop-blur-md border-t border-white/10 flex items-center justify-end gap-3">
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="venue-save"
                  className="inline-flex items-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-60 px-8 py-4 text-xs uppercase tracking-[0.24em] font-bold transition-colors"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </PortalShell>
  );
}

const inputCls =
  "w-full bg-black border border-white/15 h-11 px-3 text-[#F5F5F0] text-sm focus:outline-none focus:border-[#D9A6F5]";

function Section({ title, icon, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-4">
        {icon} {title}
      </div>
      {children}
    </section>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, hint, icon, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] mb-2">
        {icon} {label}
      </div>
      {children}
      {hint && <div className="mt-1 text-[10px] text-[#A3A3A3]/80 font-mono">{hint}</div>}
    </div>
  );
}

function MediaCard({ label, testId, src, uploading, onPick, aspect }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/10 p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.28em] text-[#A3A3A3] mb-3">{label}</div>
      <div className={`${aspect} bg-black border border-white/10 flex items-center justify-center overflow-hidden mb-3`}>
        {src ? (
          <img src={src} alt={label} className="w-full h-full object-cover" />
        ) : (
          <ImageIcon size={28} className="text-[#A3A3A3]/40" />
        )}
      </div>
      <button
        type="button"
        onClick={onPick}
        disabled={uploading}
        data-testid={`${testId}-upload`}
        className="w-full inline-flex items-center justify-center gap-2 border border-white/15 hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-4 py-3 text-[10px] uppercase tracking-[0.24em] font-bold transition-colors disabled:opacity-60"
      >
        {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
        {uploading ? "Uploading…" : src ? "Replace" : "Upload"}
      </button>
    </div>
  );
}
